import VerifiedIcon from "@mui/icons-material/DoneOutlined";
import { Avatar, Box, Chip, Grid, Tooltip } from "@mui/material";
import Flex from "../../../../../components/Flex";
// import UpsertPatientForm from "../AddNewPatient";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { GridRowSelectionModel, GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import ElevatedPaper from "../../../../../components/ElevatedPaper";
import PatientFilter, {
  FilterData,
} from "../../../../../components/PatientFilter";
// import RenderWithTooltip from "../../../../../components/RenderWithTooltip";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import { GlobalTableHeightWrapper } from "../../../../../components/GlobalTableHeightWrapper";
import SearchInput from "../../../../../components/SearchInput";
import SuccessModal, {
  useSuccess,
} from "../../../../../components/SuccessModal";
import Text from "../../../../../components/Text";
import CustomTitle from "../../../../../components/app/CustomTitle";
import { NewPatientForm } from "../../../../../constants/provider/patient/add-new-patient/add-new-patient-form";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { downloadPdf } from "../../../../../hooks/use-download-pdf";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { usePatientsList } from "../../../../../queries/use-get-patients";
import { useExportPatient } from "../../../../../queries/use-patient-export";
import { ExportPatientExportTypeEnum } from "../../../../../services";
import {
  formatDate,
  formatDateTime,
} from "../../../../../utils/date-time-utility";
import { PatientName } from "../../../../../utils/util-components/UtilComponents";
import {
  DGT_Pagination,
  applyDefaultColumnSettings,
  getAge,
  getShortenGender,
  getUserAlias,
} from "../../../../../utils/utils";
import PatientExport from "./PatientExport";
import { getStoredRealm } from "@/utils/auth-storage";

const PatientList = () => {
  const StatusMapper = {
    active: true,
    inactive: false,
  };
  const xTENANTID = getStoredRealm();
  const showSnackbar = useSnackbar();
  const [patientFilter, setPatientFilter] = useState<FilterData>();
  const [searchKey, setSearchKey, instantSearchVal] = useDebounce<string>("");
  // const { hasView } = useRoleController("Invite Patient");

  const [pagination, setPagination] = useState<DGT_Pagination>({
    page: 0,
    pageSize: 25,
  });
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { data: patientList, isLoading: tableDataLoading } = usePatientsList({
    gridSorting,
    pagination,
    searchKey: searchKey ? searchKey : undefined,
    providerUuid: patientFilter?.primary_care_provider ?? undefined,
    referringAgencyUuid: patientFilter?.referring_agency?.length
      ? patientFilter.referring_agency.map((agency) => agency.value)
      : undefined,
    ageFilterCategory: patientFilter?.age?.category,
    ageFilterValue: patientFilter?.age?.val,
    statusKey:
      StatusMapper[patientFilter?.status as keyof typeof StatusMapper] ??
      undefined,
    providerGroupId: xTENANTID,
  });
  void patientList;
  void tableDataLoading;
  void setPagination;
  void setGridSorting;
  const { mutateAsync: exportPatient } = useExportPatient();
  const [
    openedInvitePatient,
    { open: _openInvitePatient, close: closeInvitePatient },
  ] = useSuccess();
  // const { mutateAsync: resendInvitationLink } = useResendLink();

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  void setRowSelectionModel;
  type PatientList = { uuid?: string } & NewPatientForm;
  // const navigate = useNavigate();

  const COLUMNS = applyDefaultColumnSettings<PatientList>(
    [
      {
        field: "ecarehealthPatientId",
        flex: 0.4,
        minWidth: 110,
        disableColumnMenu: true,
        headerName: "Patient ID",
        renderCell: ({ value }) => (
          <Text isTabletext>{value ? value : "-"}</Text>
        ),
      },
      // {
      //   field: "flag",
      //   flex: 0.4,
      //   minWidth: 90,
      //   disableColumnMenu: true,
      //   headerName: "Flag",
      //   renderCell: () => "-",
      // },
      {
        field: "patientName",
        flex: 0.6,
        minWidth: 210,
        disableColumnMenu: true,
        headerName: "Name",
        renderCell: ({ row }) => {
          const firstName = row?.firstName ?? "";
          const lastName = row?.lastName ?? "";
          const initials = getUserAlias(firstName, lastName);
          return (
            <Flex gap={1.5} alignItems={"center"}>
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: "10px",
                  fontWeight: 400,
                  bgcolor: "#E7E7E7",
                  color: "#0E151D",
                }}
              >
                {initials}
              </Avatar>
              <PatientName
                name={`${firstName}  ${lastName}`}
                gender={row?.gender}
                patientId={row?.uuid ?? ""}
              />
            </Flex>
          );
        },
      },
      {
        field: "birthDate",
        flex: 0.4,
        minWidth: 140,
        headerName: "Age",
        renderCell: ({ row }) => (
          <Text isTabletext>
            {row?.birthDate
              ? getAge(row.birthDate) +
                "(" +
                getShortenGender(row?.gender) +
                ")"
              : "-"}
          </Text>
        ),
      },
      {
        field: "phoneNumber",
        flex: 0.4,
        minWidth: 100,
        headerName: "Phone",
        renderCell: ({ row }) => {
          return (
            <Text isTabletext>
              {row?.mobileNumber ? row?.mobileNumber : "-"}
            </Text>
          );
        },
      },
      {
        field: "email",
        flex: 0.7,
        minWidth: 200,
        disableColumnMenu: true,
        headerName: "Email",
        renderCell: ({ row }) => {
          return (
            // <RenderWithTooltip value={row?.email} />
            <Tooltip title={row?.email}>
              <Text isTabletext> {row?.email}</Text>
            </Tooltip>
          );
        },
      },
      {
        field: "providerId",
        flex: 0.6,
        minWidth: 180,
        disableColumnMenu: true,
        headerName: "Provider",
        renderCell: ({ row }) => {
          const values = Object.values(row.providerId ?? {});
          return (
            <Box>
              <Text isTabletext marginBottom={"3px"}>
                {values && values[0]}
              </Text>
              {/* <Text sx={{ fontSize: "16px", color: "#1A1A1A99" }}> */}
              {/* todo need to add speciality */}
              {/* {row.value.speciality} */}
              {/* </Text> */}
            </Box>
          );
        },
      },
      {
        field: "nextAppointment",
        flex: 0.5,
        minWidth: 160,
        disableColumnMenu: true,
        headerName: "Next Appointment",
        renderCell: ({ value }) => (
          <Text isTabletext>{value ? formatDate(value) : "-"}</Text>
        ),
      },
      {
        field: "lastAppointment",
        flex: 0.4,
        minWidth: 160,
        disableColumnMenu: true,
        headerName: "Last Appointment",
        renderCell: ({ value }) => (
          <Text isTabletext>{value ? formatDate(value) : "-"}</Text>
        ),
      },
      {
        field: "validInsurance",
        flex: 0.4,
        minWidth: 120,
        disableColumnMenu: true,
        headerName: "Valid Insurance",
        align: "center",
        // ECRH-5111 #6.1: drive the indicator off the BE-provided
        // eligibilityVerified flag (true only after a successful eligibility
        // check) instead of merely whether any insurance record exists.
        renderCell: ({ row }) =>
          (row as any)?.eligibilityVerified ? (
            <Tooltip title="Insurance eligibility verified" placement="top">
              <Flex alignItems="center" gap={0.5}>
                <VerifiedIcon color="success" fontSize="small" />
                <Text isTabletext>Verified</Text>
              </Flex>
            </Tooltip>
          ) : (
            <Tooltip
              title="No successful eligibility check on file"
              placement="top"
            >
              <Flex alignItems="center" gap={0.5}>
                <WarningAmberRoundedIcon
                  fontSize="small"
                  sx={{ color: "#FFAA00" }}
                />
                <Text isTabletext>Not Verified</Text>
              </Flex>
            </Tooltip>
          ),
      },
      {
        field: "active",
        width: 100,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        headerName: "Status",
        sortable: false,
        renderCell: ({ value }) => (
          <Chip
            size="small"
            style={{
              backgroundColor: value ? "#E1FCDE" : "#FFF2F3",
              color: value ? "#049B22" : "#B1000F",
            }}
            label={value ? "Active" : "InActive"}
          />
        ),
      },
    ],
    ["patientName", "nextAppointment", "lastAppointment"]
  );

  // const handleSendInviteLink = () => {
  //   resendInvitationLink({
  //     linkType: "set",
  //     setPasswordRequest: {
  //       email: patientList?.data?.content?.find(
  //         (it) => it?.uuid === rowSelectionModel[0]
  //       )?.email,
  //     },
  //     xTENANTID: getXtenantId(),
  //   }).then(() => openInvitePatient());
  // };

  const handleExportPatient = (selected: ExportPatientExportTypeEnum) => {
    if (rowSelectionModel.length === 0) {
      showSnackbar({
        message:
          "Please select at least one patient from the list to proceed with export",
        type: "error",
      });
      return;
    }

    let responseType = "application/pdf";
    if (selected === "CSV") responseType = "text/csv";
    else if (selected === "XLS") responseType = "application/vnd.ms-excel";

    exportPatient({
      requestBody: rowSelectionModel as Array<string>,
      exportType: selected as any,
      xTENANTID: getStoredRealm() ?? "",
    }).then((resp: any) => {
      const blobFile = new Blob([resp.data], {
        type: responseType,
      });

      downloadPdf(
        blobFile,
        `PatientList.${formatDateTime(new Date())}.${selected.toLowerCase()}`
      );
    });
  };

  return (
    <ElevatedPaper>
      <SuccessModal
        label="The invitation link has been sent successfully to Patient."
        opened={openedInvitePatient}
        onClose={closeInvitePatient}
      />

      <Flex
        justifyContent={"space-between"}
        alignItems={"center"}
        width={"100%"}
      >
        <CustomTitle>Patients</CustomTitle>
        <Flex gap={2} alignItems={"center"}>
          {/* {rowSelectionModel?.length === 1 && hasView && (
            <StyledButtonV2
              onClick={handleSendInviteLink}
              sIcon={<ForwardToInboxIcon />}
              label="Invite Patient"
            />
          )} */}
          <Box width={"20rem"}>
            <SearchInput
              searchKey={instantSearchVal}
              onSearch={setSearchKey}
              placeholder="Search Patient by Name, MRN, ID"
              // endIcon={<Search />}
            />
          </Box>
          <Box>
            <PatientExport
              disabled={rowSelectionModel.length === 0}
              onClick={(selected) => handleExportPatient(selected)}
            />
          </Box>

          <Grid item xs={3}>
            <PatientFilter
              onApply={(v) => setPatientFilter(v)}
              setPatientFilter={setPatientFilter}
              patientFilter={patientFilter}
            />
          </Grid>
        </Flex>
      </Flex>
      {/* <ActiveFilters
        filters={patientFilter ?? {}}
        setFilters={setPatientFilter as any}
      /> */}
      <GlobalTableHeightWrapper>
        <CustomDataGridV2
          searchString={searchKey}
          loading={tableDataLoading}
          rows={patientList?.data?.content ?? []}
          getRowId={(row) => row?.uuid}
          columns={COLUMNS}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
          paginationModel={pagination}
          rowSelectionModel={rowSelectionModel}
          keepNonExistentRowsSelected
          onRowSelectionModelChange={(rowSelected) => {
            setRowSelectionModel(rowSelected);
          }}
          rowCount={patientList?.data.page.totalElements}
          checkboxSelection
          rowSelection
        />
      </GlobalTableHeightWrapper>
    </ElevatedPaper>
  );
};

export default PatientList;
